import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'react-toastify';
import API_URL from '../config/api';
import { 
  CalendarDaysIcon,
  CalendarIcon, 
  ClockIcon, 
  CheckCircleIcon, 
  XCircleIcon,
  ShieldExclamationIcon,
  PlusIcon,
  BriefcaseIcon,
  XMarkIcon,
  PhotoIcon,
  TrashIcon,
  CloudArrowUpIcon,
  ArrowUpTrayIcon,
  MapPinIcon,
  EyeIcon
} from '@heroicons/react/24/outline';

const HumanResources = () => {
  const { user, isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState('time-off');
  const [timeOffRequests, setTimeOffRequests] = useState([]);
  const [accidents, setAccidents] = useState([]);
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [showAccidentModal, setShowAccidentModal] = useState(false);
  const [showAccidentDetail, setShowAccidentDetail] = useState(false);
  const [selectedAccident, setSelectedAccident] = useState(null);
  const [accidentPhotos, setAccidentPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  
  const [formData, setFormData] = useState({
    request_type: 'PTO',
    start_date: '',
    end_date: '',
    reason: ''
  });

  const [accidentFormData, setAccidentFormData] = useState({
    accident_date: new Date().toISOString().split('T')[0],
    accident_time: new Date().toTimeString().split(' ')[0].substring(0, 5),
    location: '',
    description: '',
    witnesses: ''
  });

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      if (activeTab === 'time-off') {
        const [requestsRes, balanceRes] = await Promise.all([
          axios.get(`${API_URL}/api/hr/time-off`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${API_URL}/api/hr/balance`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);
        setTimeOffRequests(requestsRes.data || []);
        setBalance(balanceRes.data);
      } else if (activeTab === 'accidents') {
        const accidentsRes = await axios.get(`${API_URL}/api/hr/accidents`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAccidents(accidentsRes.data || []);
      }
    } catch (error) {
      console.error('Error fetching HR data:', error);
      toast.error('Failed to load HR data');
    } finally {
      setLoading(false);
    }
  };

  const fetchAccidentPhotos = async (accidentId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/photos/accident/${accidentId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAccidentPhotos(data.photos || []);
      }
    } catch (error) {
      console.error('Error fetching accident photos:', error);
    }
  };

  const viewAccidentDetails = async (accident) => {
    setSelectedAccident(accident);
    setShowAccidentDetail(true);
    await fetchAccidentPhotos(accident.id);
  };

  const handleAccidentPhotoUpload = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    const token = localStorage.getItem('token');

    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('photo', file);
        formData.append('caption', '');

        const response = await fetch(`${API_URL}/api/photos/accident/${selectedAccident.id}`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });

        if (!response.ok) {
          throw new Error('Failed to upload photo');
        }
      }

      toast.success(`${files.length} photo(s) uploaded successfully!`);
      await fetchAccidentPhotos(selectedAccident.id);
    } catch (error) {
      toast.error('Failed to upload photos');
      console.error('Upload error:', error);
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const deleteAccidentPhoto = async (photoId) => {
    if (!window.confirm('Are you sure you want to delete this photo?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/photos/accident/${photoId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Photo deleted');
        await fetchAccidentPhotos(selectedAccident.id);
      } else {
        toast.error('Failed to delete photo');
      }
    } catch (error) {
      toast.error('Error deleting photo');
    }
  };

  const deleteAccident = async (id) => {
    if (!window.confirm('Are you sure you want to delete this accident report? This will also delete all associated photos.')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/hr/accidents/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Accident report deleted');
        setShowAccidentDetail(false);
        fetchData();
      } else {
        toast.error('Failed to delete accident report');
      }
    } catch (error) {
      toast.error('Error deleting accident report');
    }
  };

  const handleSubmitAccident = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/hr/accidents`, accidentFormData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Accident report submitted successfully');
      setShowAccidentModal(false);
      setAccidentFormData({
        accident_date: new Date().toISOString().split('T')[0],
        accident_time: new Date().toTimeString().split(' ')[0].substring(0, 5),
        location: '',
        description: '',
        witnesses: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to submit accident report');
    }
  };

  const handleSubmitRequest = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/hr/time-off`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.warning) {
        toast.warning(response.data.warning);
      } else {
        toast.success('Time-off request submitted successfully');
      }
      
      setShowRequestModal(false);
      setFormData({
        request_type: 'PTO',
        start_date: '',
        end_date: '',
        reason: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to submit request');
    }
  };

  const handleApprove = async (requestId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/hr/time-off/${requestId}`, 
        { status: 'approved' },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success('Request approved');
      fetchData();
    } catch (error) {
      toast.error('Failed to approve request');
    }
  };

  const handleDeny = async (requestId) => {
    const adminNotes = prompt('Enter reason for denial (optional):');
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/hr/time-off/${requestId}`, 
        { status: 'denied', admin_notes: adminNotes },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success('Request denied');
      fetchData();
    } catch (error) {
      toast.error('Failed to deny request');
    }
  };

  const handleCancel = async (requestId) => {
    if (!window.confirm('Are you sure you want to cancel this request?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/hr/time-off/${requestId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Request cancelled');
      fetchData();
    } catch (error) {
      toast.error('Failed to cancel request');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      denied: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800',
      submitted: 'bg-blue-100 text-blue-800',
      under_review: 'bg-orange-100 text-orange-800',
      resolved: 'bg-green-100 text-green-800'
    };
    return badges[status] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return <div className="spinner"></div>;
  }

  return (
    <div className="dashboard">
      <div className="page-header" style={{ marginBottom: 24 }}>
        <h1 className="page-title" style={{ fontSize: 28, fontWeight: 600, marginBottom: 8 }}>Human Resources</h1>
        <p style={{ color: '#718096', fontSize: 14 }}>Manage time off requests and accident reports</p>
      </div>

      {!isAdmin && balance && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 20, marginBottom: 32 }}>
          <div className="stat-card" style={{ background: 'white', borderRadius: 8, padding: 20, display: 'flex', alignItems: 'center', gap: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <div style={{ width: 56, height: 56, borderRadius: 12, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CalendarDaysIcon style={{ width: 28, height: 28, color: 'white' }} />
            </div>
            <div>
              <div style={{ fontSize: 28, fontWeight: 600 }}>{(balance.pto_total - balance.pto_used).toFixed(1)}</div>
              <div style={{ fontSize: 14, color: '#4a5568' }}>PTO Days Available</div>
              <div style={{ fontSize: 12, color: '#a0aec0' }}>of {balance.pto_total} days</div>
            </div>
          </div>

          <div className="stat-card" style={{ background: 'white', borderRadius: 8, padding: 20, display: 'flex', alignItems: 'center', gap: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <div style={{ width: 56, height: 56, borderRadius: 12, background: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <BriefcaseIcon style={{ width: 28, height: 28, color: 'white' }} />
            </div>
            <div>
              <div style={{ fontSize: 28, fontWeight: 600 }}>{(balance.sick_total - balance.sick_used).toFixed(1)}</div>
              <div style={{ fontSize: 14, color: '#4a5568' }}>Sick Days Available</div>
              <div style={{ fontSize: 12, color: '#a0aec0' }}>of {balance.sick_total} days</div>
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <div style={{ borderBottom: '1px solid #e2e8f0' }}>
          <div style={{ display: 'flex', gap: 0 }}>
            <button
              onClick={() => setActiveTab('time-off')}
              style={{
                flex: 1,
                padding: '16px 24px',
                background: activeTab === 'time-off' ? 'white' : 'transparent',
                border: 'none',
                borderBottom: activeTab === 'time-off' ? '2px solid #2B7FE0' : '2px solid transparent',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 12,
                fontSize: 14,
                fontWeight: 500,
                color: activeTab === 'time-off' ? '#2B7FE0' : '#718096',
                transition: 'all 0.2s'
              }}
            >
              <div style={{ 
                width: 40, 
                height: 40, 
                borderRadius: 10, 
                background: activeTab === 'time-off' 
                  ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' 
                  : '#e2e8f0', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                transition: 'all 0.3s'
              }}>
                <CalendarDaysIcon style={{ width: 22, height: 22, color: 'white' }} />
              </div>
              Time Off Requests
            </button>
            <button
              onClick={() => setActiveTab('accidents')}
              style={{
                flex: 1,
                padding: '16px 24px',
                background: activeTab === 'accidents' ? 'white' : 'transparent',
                border: 'none',
                borderBottom: activeTab === 'accidents' ? '2px solid #2B7FE0' : '2px solid transparent',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 12,
                fontSize: 14,
                fontWeight: 500,
                color: activeTab === 'accidents' ? '#2B7FE0' : '#718096',
                transition: 'all 0.2s'
              }}
            >
              <div style={{ 
                width: 40, 
                height: 40, 
                borderRadius: 10, 
                background: activeTab === 'accidents' 
                  ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' 
                  : '#e2e8f0', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                transition: 'all 0.3s'
              }}>
                <ShieldExclamationIcon style={{ width: 22, height: 22, color: 'white' }} />
              </div>
              Accident Reports
            </button>
          </div>
        </div>

        {activeTab === 'time-off' && (
          <div className="card-body">
            <div className="card-header" style={{ marginBottom: 0 }}>
              <h2 className="card-title">
                {isAdmin ? 'All Time Off Requests' : 'My Time Off Requests'}
              </h2>
              {!isAdmin && (
                <button
                  onClick={() => setShowRequestModal(true)}
                  style={{
                    background: '#2B7FE0',
                    color: 'white',
                    padding: '10px 20px',
                    borderRadius: 6,
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    fontSize: 14,
                    fontWeight: 500
                  }}
                >
                  <PlusIcon style={{ width: 18, height: 18 }} />
                  Request Time Off
                </button>
              )}
            </div>

            <div style={{ marginTop: 24 }}>
              {timeOffRequests.length === 0 ? (
                <div style={{ padding: 40, textAlign: 'center', color: '#718096' }}>
                  <p>No time off requests found</p>
                </div>
              ) : (
                timeOffRequests.map((request) => (
                  <div key={request.id} style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: 16,
                    background: '#f7fafc',
                    borderRadius: 8,
                    marginBottom: 12,
                    border: '1px solid #e2e8f0'
                  }}>
                    <div style={{ flex: 1 }}>
                      <div className="flex items-center gap-3 mb-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusBadge(request.status)}`}>
                          {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                        </span>
                        <span style={{ fontWeight: 600 }}>{request.request_type}</span>
                        {isAdmin && <span style={{ fontSize: 14, color: '#718096' }}>• {request.user_name}</span>}
                      </div>
                      
                      <div style={{ display: 'flex', alignItems: 'center', gap: 16, fontSize: 14, color: '#4a5568', marginBottom: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <CalendarIcon style={{ width: 16, height: 16 }} />
                          {formatDate(request.start_date)} - {formatDate(request.end_date)}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <ClockIcon style={{ width: 16, height: 16 }} />
                          {request.days_count} day{request.days_count !== 1 ? 's' : ''}
                        </div>
                      </div>

                      {request.reason && (
                        <p style={{ fontSize: 13, color: '#718096', marginTop: 8 }}>
                          <strong>Reason:</strong> {request.reason}
                        </p>
                      )}

                      {request.admin_notes && (
                        <p style={{ fontSize: 13, color: '#718096', marginTop: 8 }}>
                          <strong>Admin Notes:</strong> {request.admin_notes}
                        </p>
                      )}

                      {request.reviewed_by_name && (
                        <p style={{ fontSize: 12, color: '#a0aec0', marginTop: 8 }}>
                          Reviewed by {request.reviewed_by_name} on {formatDate(request.reviewed_at)}
                        </p>
                      )}
                    </div>

                    <div style={{ display: 'flex', gap: 12, marginLeft: 20 }}>
                      {isAdmin && request.status === 'pending' && (
                        <>
                          <button
                            onClick={() => handleApprove(request.id)}
                            className="btn btn-secondary"
                            style={{ 
                              padding: '8px 12px', 
                              fontSize: 13,
                              background: '#10b981',
                              color: 'white',
                              border: 'none'
                            }}
                            title="Approve"
                          >
                            <CheckCircleIcon style={{ width: 18, height: 18 }} />
                          </button>
                          <button
                            onClick={() => handleDeny(request.id)}
                            className="btn btn-secondary"
                            style={{ 
                              padding: '8px 12px', 
                              fontSize: 13,
                              background: '#ef4444',
                              color: 'white',
                              border: 'none'
                            }}
                            title="Deny"
                          >
                            <XCircleIcon style={{ width: 18, height: 18 }} />
                          </button>
                        </>
                      )}
                      {!isAdmin && ['pending', 'approved'].includes(request.status) && (
                        <button
                          onClick={() => handleCancel(request.id)}
                          className="btn btn-secondary"
                          style={{ padding: '8px 16px', fontSize: 13 }}
                          title="Cancel Request"
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {activeTab === 'accidents' && (
          <div className="card-body">
            <div className="card-header" style={{ marginBottom: 0 }}>
              <h2 className="card-title">
                {isAdmin ? 'All Accident Reports' : 'My Accident Reports'}
              </h2>
              <button
                onClick={() => setShowAccidentModal(true)}
                style={{
                  background: '#dc2626',
                  color: 'white',
                  padding: '10px 20px',
                  borderRadius: 6,
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  fontSize: 14,
                  fontWeight: 500
                }}
              >
                <PlusIcon style={{ width: 18, height: 18 }} />
                Report Accident
              </button>
            </div>

            <div style={{ marginTop: 24 }}>
              {accidents.length === 0 ? (
                <div style={{ padding: 40, textAlign: 'center', color: '#718096' }}>
                  <ShieldExclamationIcon style={{ width: 48, height: 48, margin: '0 auto 16px', color: '#dc2626', opacity: 0.5 }} />
                  <p>No accident reports found</p>
                </div>
              ) : (
                accidents.map((accident) => (
                  <div key={accident.id} style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: 16,
                    background: '#fef2f2',
                    borderRadius: 8,
                    marginBottom: 12,
                    border: '1px solid #fecaca'
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusBadge(accident.status)}`}>
                          {accident.status.replace('_', ' ').toUpperCase()}
                        </span>
                        {isAdmin && <span style={{ fontSize: 14, color: '#991b1b', fontWeight: 600 }}>• {accident.user_name}</span>}
                      </div>
                      
                      <div style={{ display: 'flex', alignItems: 'center', gap: 16, fontSize: 14, color: '#991b1b', marginBottom: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <CalendarIcon style={{ width: 16, height: 16 }} />
                          {formatDate(accident.accident_date)} at {accident.accident_time}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <MapPinIcon style={{ width: 16, height: 16 }} />
                          {accident.location}
                        </div>
                      </div>

                      <p style={{ fontSize: 14, color: '#7f1d1d', marginTop: 8 }}>
                        {accident.description.substring(0, 100)}{accident.description.length > 100 ? '...' : ''}
                      </p>
                    </div>

                    <div style={{ display: 'flex', gap: 12, marginLeft: 20 }}>
                      <button
                        onClick={() => viewAccidentDetails(accident)}
                        style={{
                          padding: '8px 16px',
                          background: '#dc2626',
                          color: 'white',
                          border: 'none',
                          borderRadius: 6,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                          fontSize: 13,
                          fontWeight: 500
                        }}
                      >
                        <EyeIcon style={{ width: 16, height: 16 }} />
                        View Details
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {/* Accident Detail Modal */}
      {showAccidentDetail && selectedAccident && (
        <div className="modal-overlay" onClick={() => setShowAccidentDetail(false)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ background: 'white', borderRadius: 12, width: '100%', maxWidth: 900, maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ padding: 24, borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'start', background: '#fef2f2' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                  <div style={{ width: 48, height: 48, borderRadius: 10, background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <ShieldExclamationIcon style={{ width: 24, height: 24, color: 'white' }} />
                  </div>
                  <div>
                    <h2 style={{ fontSize: 24, fontWeight: 600, color: '#991b1b' }}>Accident Report</h2>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusBadge(selectedAccident.status)}`}>
                      {selectedAccident.status.replace('_', ' ').toUpperCase()}
                    </span>
                  </div>
                </div>
              </div>
              <button onClick={() => setShowAccidentDetail(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
                <XMarkIcon style={{ width: 24, height: 24 }} />
              </button>
            </div>

            <div style={{ padding: 24 }}>
              <div style={{ marginBottom: 32 }}>
                <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>Incident Details</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <div>
                    <label style={{ fontSize: 13, color: '#718096', fontWeight: 500 }}>Date & Time</label>
                    <p style={{ fontSize: 15, color: '#1f2937', marginTop: 4 }}>
                      {formatDate(selectedAccident.accident_date)} at {selectedAccident.accident_time}
                    </p>
                  </div>
                  <div>
                    <label style={{ fontSize: 13, color: '#718096', fontWeight: 500 }}>Location</label>
                    <p style={{ fontSize: 15, color: '#1f2937', marginTop: 4 }}>{selectedAccident.location}</p>
                  </div>
                  {isAdmin && (
                    <div>
                      <label style={{ fontSize: 13, color: '#718096', fontWeight: 500 }}>Reported By</label>
                      <p style={{ fontSize: 15, color: '#1f2937', marginTop: 4 }}>{selectedAccident.user_name}</p>
                    </div>
                  )}
                  <div>
                    <label style={{ fontSize: 13, color: '#718096', fontWeight: 500 }}>Submitted</label>
                    <p style={{ fontSize: 15, color: '#1f2937', marginTop: 4 }}>{formatDate(selectedAccident.created_at)}</p>
                  </div>
                </div>
                <div style={{ marginTop: 16 }}>
                  <label style={{ fontSize: 13, color: '#718096', fontWeight: 500 }}>Description</label>
                  <p style={{ fontSize: 15, color: '#1f2937', marginTop: 4, lineHeight: 1.6 }}>{selectedAccident.description}</p>
                </div>
                {selectedAccident.witnesses && (
                  <div style={{ marginTop: 16 }}>
                    <label style={{ fontSize: 13, color: '#718096', fontWeight: 500 }}>Witnesses</label>
                    <p style={{ fontSize: 15, color: '#1f2937', marginTop: 4 }}>{selectedAccident.witnesses}</p>
                  </div>
                )}
              </div>

              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <h3 style={{ fontSize: 16, fontWeight: 600 }}>Photos ({accidentPhotos.length})</h3>
                  <label style={{
                    padding: '8px 16px',
                    background: '#dc2626',
                    color: 'white',
                    borderRadius: 6,
                    cursor: uploading ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    fontSize: 14,
                    opacity: uploading ? 0.6 : 1
                  }}>
                    {uploading ? (
                      <>
                        <CloudArrowUpIcon style={{ width: 18, height: 18 }} />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <ArrowUpTrayIcon style={{ width: 18, height: 18 }} />
                        Upload Photos
                      </>
                    )}
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handleAccidentPhotoUpload}
                      disabled={uploading}
                      style={{ display: 'none' }}
                    />
                  </label>
                </div>

                {accidentPhotos.length === 0 ? (
                  <div style={{ padding: 40, textAlign: 'center', background: '#f7fafc', borderRadius: 8, border: '2px dashed #cbd5e0' }}>
                    <PhotoIcon style={{ width: 48, height: 48, margin: '0 auto 12px', color: '#a0aec0' }} />
                    <p style={{ color: '#718096', marginBottom: 8 }}>No photos attached</p>
                    <p style={{ fontSize: 13, color: '#a0aec0' }}>Upload photos to document the accident</p>
                  </div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 12 }}>
                    {accidentPhotos.map(photo => (
                      <div key={photo.id} style={{ position: 'relative', paddingBottom: '100%', borderRadius: 8, overflow: 'hidden', background: '#f7fafc' }}>
                        <img 
                          src={photo.photo_url} 
                          alt="Accident" 
                          style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                        {(isAdmin || user?.id === photo.uploaded_by) && (
                          <button
                            onClick={() => deleteAccidentPhoto(photo.id)}
                            style={{
                              position: 'absolute',
                              top: 8,
                              right: 8,
                              background: 'rgba(220, 38, 38, 0.9)',
                              color: 'white',
                              border: 'none',
                              borderRadius: 4,
                              padding: 6,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            <TrashIcon style={{ width: 14, height: 14 }} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {isAdmin && (
              <div style={{ padding: 24, borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between' }}>
                <button
                  onClick={() => deleteAccident(selectedAccident.id)}
                  style={{
                    padding: '10px 20px',
                    background: '#dc2626',
                    color: 'white',
                    border: 'none',
                    borderRadius: 6,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    fontSize: 14,
                    fontWeight: 500
                  }}
                >
                  <TrashIcon style={{ width: 18, height: 18 }} />
                  Delete Report
                </button>
                <button
                  onClick={() => setShowAccidentDetail(false)}
                  style={{
                    padding: '10px 20px',
                    background: '#e2e8f0',
                    color: '#1f2937',
                    border: 'none',
                    borderRadius: 6,
                    cursor: 'pointer',
                    fontSize: 14,
                    fontWeight: 500
                  }}
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Create Accident Report Modal */}
      {showAccidentModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', borderRadius: 12, width: '90%', maxWidth: 600, boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)' }}>
            <div style={{ padding: '24px 24px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'start', justifyContent: 'space-between', background: '#fef2f2' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 48, height: 48, borderRadius: 10, background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <ShieldExclamationIcon style={{ width: 24, height: 24, color: 'white' }} />
                </div>
                <div>
                  <h3 style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>Report Accident</h3>
                  <p style={{ fontSize: 13, color: '#991b1b', margin: '2px 0 0' }}>Document workplace incident</p>
                </div>
              </div>
              <button onClick={() => setShowAccidentModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: '#991b1b' }}>
                <XMarkIcon style={{ width: 24, height: 24 }} />
              </button>
            </div>

            <form onSubmit={handleSubmitAccident}>
              <div style={{ padding: 24 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 14, fontWeight: 500, color: '#374151', marginBottom: 8 }}>Date *</label>
                    <input
                      type="date"
                      style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
                      value={accidentFormData.accident_date}
                      onChange={(e) => setAccidentFormData({ ...accidentFormData, accident_date: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 14, fontWeight: 500, color: '#374151', marginBottom: 8 }}>Time *</label>
                    <input
                      type="time"
                      style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
                      value={accidentFormData.accident_time}
                      onChange={(e) => setAccidentFormData({ ...accidentFormData, accident_time: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div style={{ marginBottom: 20 }}>
                  <label style={{ display: 'block', fontSize: 14, fontWeight: 500, color: '#374151', marginBottom: 8 }}>Location *</label>
                  <input
                    type="text"
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
                    value={accidentFormData.location}
                    onChange={(e) => setAccidentFormData({ ...accidentFormData, location: e.target.value })}
                    placeholder="Where did the accident occur?"
                    required
                  />
                </div>

                <div style={{ marginBottom: 20 }}>
                  <label style={{ display: 'block', fontSize: 14, fontWeight: 500, color: '#374151', marginBottom: 8 }}>Description *</label>
                  <textarea
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, fontFamily: 'inherit', resize: 'vertical', minHeight: 100 }}
                    value={accidentFormData.description}
                    onChange={(e) => setAccidentFormData({ ...accidentFormData, description: e.target.value })}
                    placeholder="Describe what happened in detail..."
                    required
                  />
                </div>

                <div style={{ marginBottom: 20 }}>
                  <label style={{ display: 'block', fontSize: 14, fontWeight: 500, color: '#374151', marginBottom: 8 }}>Witnesses (Optional)</label>
                  <input
                    type="text"
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
                    value={accidentFormData.witnesses}
                    onChange={(e) => setAccidentFormData({ ...accidentFormData, witnesses: e.target.value })}
                    placeholder="Names of any witnesses"
                  />
                </div>

                <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: 12, fontSize: 13, color: '#991b1b' }}>
                  <strong>Note:</strong> After submitting, you can add photos to document the incident.
                </div>
              </div>

              <div style={{ padding: '16px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setShowAccidentModal(false)} style={{ padding: '10px 20px', borderRadius: 6, border: '1px solid #d1d5db', background: 'white', color: '#374151', fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>
                  Cancel
                </button>
                <button type="submit" style={{ padding: '10px 24px', borderRadius: 6, border: 'none', background: '#dc2626', color: 'white', fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>
                  Submit Report
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Time Off Request Modal (existing code preserved) */}
      {showRequestModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', borderRadius: 12, width: '90%', maxWidth: 500, boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)' }}>
            <div style={{ padding: '24px 24px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'start', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 48, height: 48, borderRadius: 10, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <CalendarDaysIcon style={{ width: 24, height: 24, color: 'white' }} />
                </div>
                <div>
                  <h3 style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>Request Time Off</h3>
                  <p style={{ fontSize: 13, color: '#718096', margin: '2px 0 0' }}>Submit your time-off request</p>
                </div>
              </div>
              <button onClick={() => setShowRequestModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: '#718096' }}>
                <XMarkIcon style={{ width: 24, height: 24 }} />
              </button>
            </div>

            <form onSubmit={handleSubmitRequest}>
              <div style={{ padding: 24 }}>
                <div style={{ marginBottom: 20 }}>
                  <label style={{ display: 'block', fontSize: 14, fontWeight: 500, color: '#374151', marginBottom: 8 }}>Request Type</label>
                  <select
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, background: 'white', cursor: 'pointer' }}
                    value={formData.request_type}
                    onChange={(e) => setFormData({ ...formData, request_type: e.target.value })}
                    required
                  >
                    <option value="PTO">PTO</option>
                    <option value="Sick Day">Sick Day</option>
                  </select>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 14, fontWeight: 500, color: '#374151', marginBottom: 8 }}>Start Date</label>
                    <input
                      type="date"
                      style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
                      value={formData.start_date}
                      onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                      required
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: 14, fontWeight: 500, color: '#374151', marginBottom: 8 }}>End Date</label>
                    <input
                      type="date"
                      style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }}
                      value={formData.end_date}
                      onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                      min={formData.start_date}
                      required
                    />
                  </div>
                </div>

                <div style={{ marginBottom: 20 }}>
                  <label style={{ display: 'block', fontSize: 14, fontWeight: 500, color: '#374151', marginBottom: 8 }}>Reason (Optional)</label>
                  <textarea
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, fontFamily: 'inherit', resize: 'vertical', minHeight: 80 }}
                    value={formData.reason}
                    onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                    placeholder="Provide a brief reason for your time off request..."
                  />
                </div>

                {balance && formData.request_type && (
                  <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 32, height: 32, borderRadius: 6, background: formData.request_type === 'PTO' ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      {formData.request_type === 'PTO' ? <CalendarDaysIcon style={{ width: 18, height: 18, color: 'white' }} /> : <BriefcaseIcon style={{ width: 18, height: 18, color: 'white' }} />}
                    </div>
                    <div>
                      <div style={{ fontSize: 12, color: '#1e40af', marginBottom: 2 }}>Available Balance</div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#1e3a8a' }}>
                        {formData.request_type === 'PTO'
                          ? (balance.pto_total - balance.pto_used).toFixed(1)
                          : (balance.sick_total - balance.sick_used).toFixed(1)}{' '}
                        {formData.request_type} days remaining
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div style={{ padding: '16px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setShowRequestModal(false)} style={{ padding: '10px 20px', borderRadius: 6, border: '1px solid #d1d5db', background: 'white', color: '#374151', fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>
                  Cancel
                </button>
                <button type="submit" style={{ padding: '10px 24px', borderRadius: 6, border: 'none', background: '#2B7FE0', color: 'white', fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>
                  Submit Request
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default HumanResources;